import { authStore } from '@/services/auth/store';
import { session } from '@/services/session';
import tracking from '@/utils/tracking-scripts';
import { useStore } from '@nanostores/react';
import { useEffect, useRef } from 'react';

/**
 * Injects the third-party tracking scripts (an external system: `<script>` tags
 * on `document`) as soon as the session resolves, rather than waiting for a user
 * to navigate to an authenticated URL.
 *
 * Both session states matter. FullStory and Zendesk load for authenticated
 * users; Google Tag Manager loads only for anonymous visitors on the login and
 * signup pages Hydra owns. Each state loads at most once, and the anonymous
 * pass is skipped entirely once the authenticated pass has run.
 */
export function useTrackingScriptsInjection() {
  const { account, user } = useStore(authStore);
  const isSessionInitialized = useStore(session.isInitialized);
  const isAuthenticated = useStore(session.isAuthenticated);
  const hasInjectedAuthenticatedScripts = useRef<boolean>(false);
  const hasInjectedAnonymousScripts = useRef<boolean>(false);

  useEffect(() => {
    if (account !== null && user !== null && !hasInjectedAuthenticatedScripts.current) {
      // We only ever want to inject once.
      hasInjectedAuthenticatedScripts.current = true;
      tracking.load({ authenticated: true, account, user });
    }
  }, [account, user]);

  useEffect(() => {
    // "Signed out" is only a fact once the session has finished bootstrapping —
    // before that it just means "not signed in yet". This mirrors the Ember
    // application route, which awaited `session.waitForInitialized` before
    // injecting the anonymous scripts.
    if (!isSessionInitialized || isAuthenticated) {
      return;
    }

    if (hasInjectedAnonymousScripts.current || hasInjectedAuthenticatedScripts.current) {
      return;
    }

    hasInjectedAnonymousScripts.current = true;
    tracking.load({ authenticated: false });
  }, [isSessionInitialized, isAuthenticated]);
}
